<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row column-align="center">
          <grid-col col-size="4">
            <h1 class="header margin-top-xl">
              <div>
                <chevron
                  @click="$router.push({name: origin})"/>
                {{getTitle}}
              </div>
              <GoToTagsAndCategorical
                origin='AggregateOperatedVariable'/>
            </h1>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <form v-on:submit.prevent v-on:keypress.enter.prevent="onSave">
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field first-operand">
                <label-variable-selector
                  :label="$t('aggregateVariable.variable')"
                  v-model="operandOne"
                  v-on:tagsChanged="tagsChanged"
                  :variables="getVariablesMinimal"
                  :taggedVariables="getTaggedVariables"
                  :tree="getTagsTree"
                  :error="operandOneError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  :disabled="isEdition()"
                  class="margin-right-s"/>
              </div>
              <div class="variable-input-field operator">
                <label-select
                  :label="$t('aggregateVariable.operator')"
                  v-model="operator"
                  :error="operatorError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  :options="operators"
                  :disabled="isEdition()"
                  form-size="m"
                  class="margin-right-s">
                </label-select>
              </div>
              <div class="variable-input-variable-or-constant second-operand">
                <div class="variable-input-field variable-input-variable-or-constant-field">
                  <label-variable-selector
                    v-if="varOrConst === 'variable'"
                    :label="$t('aggregateVariable.variable')"
                    v-model="operandTwo"
                    :error="operandTwoError"
                    :errorMessage="$t('commons.mandatoryFieldMessage')"
                    v-on:tagsChanged="tagsChanged"
                    :variables="getVariablesMinimal"
                    :taggedVariables="getTaggedVariables"
                    :tree="getTagsTree"
                    :disabled="isEdition()" />
                  <label-input
                    v-if="varOrConst === 'constant'"
                    :label="$t('aggregateVariable.constant')"
                    :type="'number'"
                    :step="'.01'"
                    v-model="constant"
                    :disabled="isEdition()"
                    :error="constantError"
                    :errorMessage="$t('commons.mandatoryFieldMessage')"
                    form-size="m">
                  </label-input>
                </div>
                <div class="variable-input-field variable-input-variable-or-constant-toggle">
                  <toggle-switch
                    :disabled="isEdition()"
                    v-on:change="onSecondOperandSelectionTypeChanged"
                    :first="radioFirst"
                    :second="radioSecond"
                    :selected="varOrConst"/>
                </div>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="variable-input-field variable-input-field-name">
                <label-input
                  :label="$t('aggregateVariable.name')"
                  v-model="name"
                  :error="nameError"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  class="margin-right-s">
                </label-input>
              </div>
              <div class="variable-input-field variable-input-field-unit">
                <label-input
                  :label="$t('aggregateVariable.unit')"
                  v-model="unit">
                </label-input>
              </div>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <grid-col
              col-size="4">
              <category-selector
                :label="$t('variableManagement.category')"
                :categories="getCategories"
                v-model="variableCategory">
              </category-selector>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <tags-editor
              :key="tagTreeKey"
              class="variable-input-field variable-input-field-name"
              :tree="getTagsTree"
              v-model="tags"/>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="align-right margin-bottom-xxl">
                <checkbox
                  id="historic"
                  class="margin-right-s"
                  :label="$t('aggregateMetricalVariable.calculateHistoricalData')"
                  v-on:change="historicalActivatedChanged"
                  :checked="false"
                  check-size="s"
                  :hidden="this.isEdition()"/>
              </div>
            </grid-col>
          </grid-row>
          <grid-row column-align="center">
            <grid-col col-size="4">
              <div class="align-right margin-bottom-l">
                <btn-pill
                  :btnText="$t('commons.cancel')"
                  v-on:click="onCancel"
                  btn-size="m"
                  btn-type="secondary">
                </btn-pill>
                <btn-pill
                  :btnText="$t('commons.save')"
                  v-on:click="onSave"
                  btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
        </form>
      </div>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Checkbox from '@/components/shared/forms/Checkbox';
import LabelSelect from '@/components/shared/forms/LabelSelect';
import Radio from '@/components/shared/forms/Radio';
import Alert from '@/components/shared/alert/Alert';
import {mapActions, mapGetters} from 'vuex';
import AcceptModal from '../../components/shared/dialogs/AcceptModal';
import ToggleSwitch from '@/components/shared/forms/ToggleSwitch';
import TagsEditor from '@/components/variable/TagsEditor';
import LabelVariableSelector from '@/components/variable/LabelVariableSelector';
import Chevron from '@/components/shared/Chevron';
import GoToTagsAndCategorical from '@/components/variable/GoToTagsAndCategorical';
import CategorySelector from '@/components/variable/CategorySelector';
import aggregateDefs from '@/layouts/variables/aggregateVariableDefs';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';

export default {
  components: {
    LabelVariableSelector,
    TagsEditor,
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    BtnPill,
    Checkbox,
    LabelSelect,
    Radio,
    Alert,
    AcceptModal,
    ToggleSwitch,
    Chevron,
    GoToTagsAndCategorical,
    CategorySelector
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      origin: 'AggregateOperatedVariableList',
      editAggregateVariable: {},
      /* Form fields */
      name: '',
      unit: '',
      variableCategory: null,
      operandOne: undefined,
      operandTwo: undefined,
      operator: {label: ' '},
      constant: undefined,
      tags: [],
      /* Form field errors */
      nameError: false,
      operandOneError: false,
      operandTwoError: false,
      operatorError: false,
      constantError: false,
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      varOrConst: 'constant',
      radioFirst: {name: 'constant', label: this.$t('aggregateVariable.constant')},
      radioSecond: {name: 'variable', label: this.$t('aggregateVariable.variable')},
      operators: [
        {
          label: '+',
          value: '+'
        },
        {
          label: '-',
          value: '-'
        },
        {
          label: '*',
          value: '*'
        },
        {
          label: '/',
          value: '/'
        }
      ],
      variablesMinimal: [],
      taggedVariables: [],
      historicalActivated: false,
      tagTreeKey: 0
    };
  },
  created () {
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }
    this.editAggregateVariable = this.$route.params;

    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      if (this.isEdition()) {
        const operandOne = getVarById(this.getVariablesMinimal, this.editAggregateVariable.varOneId);
        const operandTwo = getVarById(this.getVariablesMinimal, this.editAggregateVariable.varTwoId);

        this.operandOne = operandOne;
        this.operandTwo = operandTwo || {label: ' '};
        this.unit = this.editAggregateVariable.unit;
        this.name = this.editAggregateVariable.name;
        this.varOrConst = operandTwo ? 'variable' : 'constant';
        this.operator = this.operators.find(operator => operator.value === this.editAggregateVariable.operator);
        this.constant = this.editAggregateVariable.constant;
        this.tags = this.editAggregateVariable.tags;
        this.tagTreeKey++;
      }
      this.retrieveTagsTree({ context: this });
    });

    this.retrieveCategories({ context: this }).then(() => {
      if (this.isEdition()) {
        this.variableCategory = getCategoryById(this.getCategories, this.editAggregateVariable.categoryId);
      }
    });
  },
  computed: {
    ...mapGetters([
      'getTagsTree',
      'getAggregateOperatedVariables',
      'getCategories'
    ]),
    getBreadcrumb () {
      switch (this.origin) {
        case 'AggregateOperatedVariableList':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('aggregateVariable.title'), link: '/aggregate-variable/menu' },
            { name: this.$t('aggregateVariable.constantVariables'), link: '/aggregate-variable/operated/list' },
            { name: this.getTitle, link: '/aggregate-variable/operated', disabled: true }
          ];

        case 'VariableTagTree':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('variables.taxonomy'), link: '/tags-tree' },
            { name: this.$t('aggregateVariable.addAggregateVariable'), disabled: true }
          ];

        case 'ExplorerEvolutionVariables':
          return [
            { name: this.$t('explorer.breadcrumbParent'), link: '/explore' },
            { name: this.$t('explorer.parameterSelection'), link: '/explorer-evolution-variables' },
            { name: this.$t('aggregateVariable.addAggregateVariable'), disabled: true }
          ];

        case 'ExplorerCorrelationVariables':
          return [
            { name: this.$t('explorer.breadcrumbParentOldExplorer'), link: '/explore' },
            { name: this.$t('explorer.parameterSelection'), link: '/explorer-correlation-variables' },
            { name: this.$t('aggregateVariable.addAggregateVariable'), disabled: true }
          ];

        case 'VariableManagement':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('aggregateVariable.addAggregateVariable'), disabled: true }
          ];
      }
    },
    getTitle () {
      return this.isEdition()
        ? this.$t('aggregateVariable.editAggregateVariable')
        : this.$t('aggregateVariable.addAggregateVariable');
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      return this.taggedVariables;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree',
      'createAggregateVariable',
      'updateAggregateVariable',
      'retrieveCategories'
    ]),
    isEdition () {
      return this.editAggregateVariable.id !== undefined;
    },
    onSave () {
      clearErrorPrompts(this);
      if (isValidForm(this)) {
        const operandOne = this.operandOne.id;
        const operandTwo = this.varOrConst === 'variable' ? this.operandTwo.id : undefined;
        const aggregateVariable = {
          name: this.name,
          unit: this.unit,
          operator: this.operator.value,
          varOneId: operandOne,
          varTwoId: operandTwo,
          constant: this.varOrConst === 'variable' ? undefined : this.constant,
          tags: this.tags,
          goal: aggregateDefs.goal.operated,
          historic: this.historicalActivated
        };
        if (this.variableCategory !== null) {
          aggregateVariable.categoryId = this.variableCategory.id;
        } else {
          aggregateVariable.categoryId = null;
        }

        if (this.isEdition()) {
          aggregateVariable.id = this.editAggregateVariable.id;
          updateAggregateVariable(this, aggregateVariable);
        } else {
          createAggregateVariable(this, aggregateVariable);
        }
      }
    },
    onCancel () {
      this.$router.push({name: this.origin});
    },
    onSecondOperandSelectionTypeChanged (event) {
      this.varOrConst = event.target.value;
    },
    hasRoute (path) {
      const configRoutes = this.$router.options.routes;
      return configRoutes.find(route => route.path === path) !== undefined;
    },
    historicalActivatedChanged: function (event) {
      this.historicalActivated = event;
    }
  }
};

const isValidForm = context => {
  let hasError = false;
  if (!context.operandOne) {
    context.operandOneError = true;
    hasError = true;
  }

  if (!context.operator || !context.operator.value) {
    context.operatorError = true;
    hasError = true;
  }

  if (context.varOrConst === 'variable' && !context.operandTwo) {
    context.operandTwoError = true;
    hasError = true;
  }

  if (context.varOrConst === 'constant' && (!context.constant || context.constant.lenght < 1)) {
    context.constantError = true;
    hasError = true;
  }

  if (!context.name || context.name.length < 1) {
    context.nameError = true;
    hasError = true;
  }
  return !hasError;
};

const clearErrorPrompts = context => {
  context.operandOneError = false;
  context.operandTwoError = false;
  context.constantError = false;
  context.nameError = false;
  context.operatorError = false;
};

const getVarById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].id) {
      return list[i];
    }
  }
};

const createAggregateVariable = (context, aggregateVariable) => {
  context.createAggregateVariable({context: context, aggregateVariable}).then(() => {
    const msg = context.$t('aggregateVariable.successCreation', {name: aggregateVariable.name});
    context.$router.replace({ name: context.origin, params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('aggregateVariable.errorCreation', {name: aggregateVariable.name}), 'error');
  });
};

const updateAggregateVariable = (context, aggregateVariable) => {
  const msg = context.$t('aggregateVariable.successUpdate', {name: aggregateVariable.name});
  context.updateAggregateVariable({context: context, aggregateVariable}).then(() => {
    context.$router.replace({ name: context.origin, params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('aggregateVariable.errorUpdate', {name: aggregateVariable.name}), 'error');
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const getCategoryById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].value) {
      return list[i];
    }
  }
  return null;
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .align-right {
    float: right;
    clear:both;
  }

  .variable-input-field {
    display: inline-block;
  }

  .variable-input-field-name {
    width: 75%;
    margin: 0 0;
  }

  .variable-input-field-unit {
    min-width: 0px;
    width: 25%;
    float: right;
    margin: 0 0;
  }

  .first-operand {
    float: left;
    width: 35%;
    margin: 0 0;
  }

  .operator {
    width: 10%;
    margin: 0 0;
  }

  .second-operand {
    width: 54.5%;
    margin: 0 0;
    float: right;
  }

  .variable-input-variable-or-constant-field {
    width: 70%;
  }

  .variable-input-variable-or-constant-toggle {
    margin-top: 2rem;
    float: right;
    display: inline-block;
  }

  .submit-buttons {
    float: right;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/variables/AggregateOperatedVariable.vue